import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import firebase from "firebase/app";

import {
  tAbandonedCart,
  tAbandonedCartRecoveryRequestBody,
  tAddressPayload,
  tAnnouncementRequestBody,
  tBasicBrand,
  tBoutique,
  tBrand,
  tBusinessError,
  tBuyerData,
  tCheckoutResponse,
  tCompanyStatsMonthlySalesResponse,
  tCompanyStatsResponse,
  tCompanyStatsSalesSnapshotResponse,
  tConfigs,
  tDetailedCart,
  tFeature,
  tInviteRetailersRequestBody,
  tLongAddress,
  tMessage,
  tMessageRequestBody,
  tPostConnectionsRequestBody,
  tPostReferralInviteRequestBody,
  tPostReferralsRequestBody,
  tProduct,
  tProductsBulkRequestBody,
  tPromotion,
  tPutOrderConfirmRepRequestBody,
  tRep,
  tRetailerApplication,
  tRetailerSettingsResponse,
  tShopifySettingsRequestBody,
  tUser,
  tVendorData,
} from "tmt_api/src/types/types.validator";
import { tPostReorderResponse, tPutOrderSplitRequestBody, tPutOrderSplitResponse } from "tmt_api/src/types/api";

import { API_URL, PDFS_URL } from "src/env";

const api = axios.create({
  baseURL: API_URL,
});

const apiPdfs = axios.create({
  baseURL: PDFS_URL,
});

const onFulfilledHeaders = async (config: AxiosRequestConfig) => {
  const token = await firebase.auth().currentUser?.getIdToken();
  if (config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(onFulfilledHeaders);

apiPdfs.interceptors.request.use(onFulfilledHeaders);

export type ApiError = AxiosError<tBusinessError>;

export const isApiError = (error: unknown): error is ApiError => {
  return axios.isAxiosError(error);
};

export const isLegacyApiError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error);
};

export const getLegacyApiError = (error: unknown): tBusinessError => {
  return {
    status: isLegacyApiError(error) ? error.response?.status ?? 500 : 500,
    message: isLegacyApiError(error)
      ? error.response?.data ?? "Something went wrong, please try again later."
      : "Something went wrong, please try again later.",
    code: "tmt.unexpected",
  };
};

export const getApiError = (error: unknown): tBusinessError => {
  return {
    status: isApiError(error) ? error.response?.data?.status ?? 500 : 500,
    message: isApiError(error)
      ? error.response?.data?.message ?? "Something went wrong, please try again later."
      : "Something went wrong, please try again later.",
    code: isApiError(error) ? error.response?.data?.code ?? "tmt.unexpected" : "tmt.unexpected",
  };
};

export const getBrandPublic = (brandId: string): Promise<tBasicBrand> =>
  api.get<tBasicBrand>(`brands/${brandId}/public`).then((res) => res.data);

export const getCartsAbandoneds = (): Promise<tAbandonedCart[]> => api.get("carts/abandoneds").then((res) => res.data);

export const postCartAbandonedRecovery = (data: tAbandonedCartRecoveryRequestBody): Promise<void> =>
  api
    .post<tAbandonedCartRecoveryRequestBody, AxiosResponse<void>>(`carts/abandoneds/recovery`, data)
    .then((res) => res.data);

export const getBrandStats = (): Promise<tCompanyStatsResponse> =>
  api.get<tCompanyStatsResponse>("brands/stats").then((res) => res.data);

export const getBrandStatsMonthlySales = (params: {
  start?: string;
  end?: string;
}): Promise<tCompanyStatsMonthlySalesResponse> =>
  api.get<tCompanyStatsMonthlySalesResponse>("brands/stats/monthly-sales", { params }).then((res) => res.data);

export const getBrandStatsSalesSnapshot = (params: {
  start?: string;
  end?: string;
}): Promise<tCompanyStatsSalesSnapshotResponse> =>
  api.get<tCompanyStatsSalesSnapshotResponse>("brands/stats/sales-snapshot", { params }).then((res) => res.data);

export const getRetailerSetupTerms = (): Promise<tBuyerData> =>
  api.get<tBuyerData>("retailers/setup-terms").then((res) => res.data);

export const getBrandSetupAccount = (): Promise<tVendorData> =>
  api.get<tVendorData>("brands/setup-account").then((res) => res.data);

export const getRepSetupAccount = (): Promise<tVendorData> =>
  api.get<tVendorData>("reps/setup-account").then((res) => res.data);

export const getRetailerSetupRewards = (): Promise<tVendorData> =>
  api.get<tVendorData>("retailers/setup-rewards").then((res) => res.data);

export const getRetailerSettings = (retailerId: string): Promise<tRetailerSettingsResponse> =>
  api.get<tRetailerSettingsResponse>(`retailers/${retailerId}/settings`).then((res) => res.data);

export const getRepStats = (): Promise<tCompanyStatsResponse> =>
  api.get<tCompanyStatsResponse>("reps/stats").then((res) => res.data);

export const getRepStatsMonthlySales = (params: {
  start?: string;
  end?: string;
}): Promise<tCompanyStatsMonthlySalesResponse> =>
  api.get<tCompanyStatsMonthlySalesResponse>("reps/stats/monthly-sales", { params }).then((res) => res.data);

export const getRepStatsSalesSnapshot = (params: {
  start?: string;
  end?: string;
}): Promise<tCompanyStatsSalesSnapshotResponse> =>
  api.get<tCompanyStatsSalesSnapshotResponse>("reps/stats/sales-snapshot", { params }).then((res) => res.data);

export const getBrand = (brandId: string): Promise<tBasicBrand> =>
  api.get<void, AxiosResponse<tBasicBrand>>(`brands/${brandId}`).then((res) => res.data);

export const getConfigs = (): Promise<tConfigs> =>
  api.get<void, AxiosResponse<tConfigs>>("configs").then((res) => res.data);

export const getRetailerAddresses = (): Promise<tLongAddress[]> =>
  api.get<void, AxiosResponse<tLongAddress[]>>("retailers/addresses").then((res) => res.data);

export const getAdminRetailerAddresses = (retailerId: string): Promise<tLongAddress[]> =>
  api.get<void, AxiosResponse<tLongAddress[]>>(`retailers/${retailerId}/addresses`).then((res) => res.data);

export const getOrderInvoice = (orderId: string): Promise<string> =>
  apiPdfs.get(`invoice/${orderId}`).then((res) => res.data);

export const getOrderPackingSlip = (orderId: string): Promise<string> =>
  apiPdfs.get(`packing-slip/${orderId}`).then((res) => res.data);

export const postBrandInviteRetailers = (
  brandId: string,
  payload: tInviteRetailersRequestBody,
): Promise<{ error: string }> =>
  api
    .post<tInviteRetailersRequestBody, AxiosResponse<{ error: string }>>(`brands/${brandId}/invite-retailers`, payload)
    .then((res) => res.data);

export const postRetailerAddress = (payload: tAddressPayload): Promise<tLongAddress[]> =>
  api.post<tAddressPayload, AxiosResponse<tLongAddress[]>>("retailers/addresses", payload).then((res) => res.data);

export const postConnections = (payload: tPostConnectionsRequestBody): Promise<void> =>
  api.post<tPostConnectionsRequestBody, AxiosResponse<void>>("connections", payload).then((res) => res.data);

export const postAdminRetailerAddress = (retailerId: string, payload: tAddressPayload): Promise<tLongAddress[]> =>
  api
    .post<tAddressPayload, AxiosResponse<tLongAddress[]>>(`retailers/${retailerId}/addresses`, payload)
    .then((res) => res.data);

export const postFeatureUsed = (retailerId: string, featureName: tFeature): Promise<void> =>
  api.post(`/retailers/${retailerId}/features-used/${featureName}`).then((res) => res.data);

export const postProduct = (payload: tProduct): Promise<void> =>
  api.post<tProduct, AxiosResponse>("/products", payload).then((res) => res.data);

export const putProduct = (payload: tProduct): Promise<void> =>
  api.put<tProduct, AxiosResponse>("/products/update", payload).then((res) => res.data);

export const putBrand = (brandId: string, payload: { brand: tBrand; user: tUser }): Promise<void> =>
  api.put<{ brand: tBrand; user: tUser }, AxiosResponse>(`brands/${brandId}/update`, payload).then((res) => res.data);

export const putRetailer = (retailerId: string, payload: { retailer: tBoutique; user: tUser }): Promise<void> =>
  api
    .put<{ retailer: tBoutique; user: tUser }, AxiosResponse>(`retailers/${retailerId}/update`, payload)
    .then((res) => res.data);

export const putRetailerReleaseCredit = (retailerId: string): Promise<void> =>
  api.put<void>(`retailers/${retailerId}/release-credit`).then((res) => res.data);

export const putRetailerAddress = (addressId: string, payload: tAddressPayload): Promise<tLongAddress[]> =>
  api
    .put<tAddressPayload, AxiosResponse<tLongAddress[]>>(`retailers/addresses/${addressId}`, payload)
    .then((res) => res.data);

export const putRetailerActivate = (retailerId: string): Promise<void> =>
  api.put<void>(`/retailers/${retailerId}/activate`).then((res) => res.data);

export const putRetailerDeactivate = (retailerId: string): Promise<void> =>
  api.put<void>(`/retailers/${retailerId}/deactivate`).then((res) => res.data);

export const putRetailerChangeEmail = (retailerId: string, newEmail: string): Promise<void> =>
  api
    .put<{ newEmail: string }, AxiosResponse<void>>(`/retailers/${retailerId}/change-email`, { newEmail })
    .then((res) => res.data);

export const putAdminRetailerAddress = (
  retailerId: string,
  addressId: string,
  payload: tAddressPayload,
): Promise<tLongAddress[]> =>
  api
    .put<tAddressPayload, AxiosResponse<tLongAddress[]>>(`retailers/${retailerId}/addresses/${addressId}`, payload)
    .then((res) => res.data);

export const putRep = (repId: string, payload: { rep: tRep; user: tUser }): Promise<void> =>
  api.put<{ rep: tRep; user: tUser }, AxiosResponse>(`reps/${repId}/update`, payload).then((res) => res.data);

export const putBrandAnnouncement = (
  brandId: string,
  announcementId: string,
  payload: tAnnouncementRequestBody,
): Promise<void> =>
  api
    .put<tAnnouncementRequestBody, AxiosResponse<void>>(`brands/${brandId}/announcements/${announcementId}`, payload)
    .then((res) => res.data);

export const deleteAdminRetailerAddress = (retailerId: string, addressId: string): Promise<tLongAddress[]> =>
  api
    .delete<void, AxiosResponse<tLongAddress[]>>(`retailers/${retailerId}/addresses/${addressId}`)
    .then((res) => res.data);

export const deleteRetailerAddress = (id: string): Promise<tLongAddress[]> =>
  api.delete<void, AxiosResponse<tLongAddress[]>>(`retailers/addresses/${id}`).then((res) => res.data);

export const postMessage = (threadId: string, data: tMessageRequestBody): Promise<tMessage> =>
  api.post<tMessageRequestBody, AxiosResponse<tMessage>>(`threads/${threadId}/message`, data).then((res) => res.data);

export const putThreadRead = (threadId: string): Promise<void> =>
  api.put(`threads/${threadId}/read`).then((res) => res.data);

export const putCartAddress = (id: string, payload: tAddressPayload): Promise<tLongAddress> =>
  api.put<tAddressPayload, AxiosResponse<tLongAddress>>(`cart/${id}/address`, payload).then((res) => res.data);

export const putCartApplyCredits = (id: string): Promise<Partial<tDetailedCart>> =>
  api.put<Partial<tDetailedCart>>(`cart/${id}/apply-credits`).then((res) => res.data);

export const postReorder = (orderId: string): Promise<tPostReorderResponse> =>
  api.post<void, AxiosResponse<tPostReorderResponse>>(`reorder/${orderId}`).then((res) => res.data);

export const putOrderSplit = (orderId: string, data: tPutOrderSplitRequestBody): Promise<tPutOrderSplitResponse> =>
  api
    .put<tPutOrderSplitRequestBody, AxiosResponse<tPutOrderSplitResponse>>(`orders/${orderId}/split`, data)
    .then((res) => res.data);

export const putOrderConfirmRep = (orderId: string, data: tPutOrderConfirmRepRequestBody): Promise<void> =>
  api
    .put<tPutOrderConfirmRepRequestBody, AxiosResponse<void>>(`orders/${orderId}/confirm-rep`, data)
    .then((res) => res.data);

export const postRetailerSetupTerms = (): Promise<string> =>
  api.post<tBuyerData, AxiosResponse<string>>("retailers/setup-terms").then((res) => res.data);

export const postRetailerLegacySetupTerms = (payload: tBuyerData): Promise<string> =>
  api.post<tBuyerData, AxiosResponse<string>>("retailers/legacy-setup-terms", payload).then((res) => res.data);

export const postBrandSetupAccount = (payload: tVendorData): Promise<void> =>
  api.post<tVendorData, AxiosResponse<void>>("brands/setup-account", payload).then((res) => res.data);

export const postBrandRetailer = (
  brandId: string,
  payload: tRetailerApplication,
): Promise<{ isExistingUser: boolean; isConnected: boolean }> =>
  api
    .post<tRetailerApplication, AxiosResponse<{ isExistingUser: boolean; isConnected: boolean }>>(
      `brands/${brandId}/retailers`,
      payload,
    )
    .then((res) => res.data);

export const postBrandAnnouncement = (brandId: string, payload: tAnnouncementRequestBody): Promise<void> =>
  api
    .post<tAnnouncementRequestBody, AxiosResponse<void>>(`brands/${brandId}/announcements`, payload)
    .then((res) => res.data);

export const postRepSetupAccount = (payload: tVendorData): Promise<void> =>
  api.post<tVendorData, AxiosResponse<void>>("reps/setup-account", payload).then((res) => res.data);

export const postRetailerSetupRewards = (payload: tVendorData): Promise<void> =>
  api.post<tVendorData, AxiosResponse<void>>("retailers/setup-rewards", payload).then((res) => res.data);

export const postProductActivate = (uid: string): Promise<void> =>
  api.post(`products/${uid}/activate`).then((res) => res.data);

export const postProductDeactivate = (uid: string): Promise<void> =>
  api.post(`products/${uid}/deactivate`).then((res) => res.data);

export const postCheckout = (brandId?: string): Promise<tCheckoutResponse> =>
  api
    .post<void, AxiosResponse<tCheckoutResponse>>(brandId ? `checkout/${brandId}` : "checkout")
    .then((res) => res.data);

export const postOrders = (payload: { cartsIds: string[] }): Promise<void> =>
  api.post<{ cartsIds: string[] }, AxiosResponse<void>>("orders", payload).then((res) => res.data);

export const postRepRetailer = (
  repId: string,
  payload: tRetailerApplication,
): Promise<{ isExistingUser: boolean; isConnected: boolean }> =>
  api
    .post<tRetailerApplication, AxiosResponse<{ isExistingUser: boolean; isConnected: boolean }>>(
      `reps/${repId}/retailers`,
      payload,
    )
    .then((res) => res.data);

export const postRepInviteRetailers = (
  repId: string,
  payload: tInviteRetailersRequestBody,
): Promise<{ error: string }> =>
  api
    .post<tInviteRetailersRequestBody, AxiosResponse<{ error: string }>>(`reps/${repId}/invite-retailers`, payload)
    .then((res) => res.data);

export const postRepInviteBrands = (
  repId: string,
  payload: tInviteRetailersRequestBody,
): Promise<{ failedEmails: string[]; existingEmails: string[] }> =>
  api
    .post<tInviteRetailersRequestBody, AxiosResponse<{ failedEmails: string[]; existingEmails: string[] }>>(
      `reps/${repId}/invite-brands`,
      payload,
    )
    .then((res) => res.data);

export const postPromotion = (payload: tPromotion): Promise<void> =>
  api.post<tPromotion, AxiosResponse<void>>("promotions", payload).then((res) => res.data);

export const putProductsBulk = (payload: tProductsBulkRequestBody): Promise<void> =>
  api.put<tProductsBulkRequestBody, AxiosResponse<void>>("products/bulk", payload).then((res) => res.data);

export const putBrandAcceptTerms = (): Promise<void> => api.put("brands/accept-terms").then((res) => res.data);

export const putRetailerAcceptTerms = (): Promise<void> => api.put("retailers/accept-terms").then((res) => res.data);

export const putPromotion = (promotionId: string, payload: tPromotion): Promise<void> =>
  api.put<tPromotion, AxiosResponse<void>>(`promotions/${promotionId}`, payload).then((res) => res.data);

export const postDraft = (cartId: string, title: string, description: string): Promise<string> =>
  api
    .post<{ title: string; description: string }, AxiosResponse<string>>(`drafts?cartId=${cartId}`, {
      title,
      description,
    })
    .then((res) => res.data);

export const postReferrals = (payload: tPostReferralsRequestBody): Promise<{ hasExistingUsers: boolean }> =>
  api
    .post<tPostReferralsRequestBody, AxiosResponse<{ hasExistingUsers: boolean }>>("/referrals", payload)
    .then((res) => res.data);

export const postReferralInvite = (referralId: string, payload: tPostReferralInviteRequestBody): Promise<void> =>
  api
    .post<tPostReferralInviteRequestBody, AxiosResponse<void>>(`/referrals/${referralId}/invite`, payload)
    .then((res) => res.data);

export const putShopifySettings = (payload: tShopifySettingsRequestBody): Promise<void> =>
  api.put<tShopifySettingsRequestBody, AxiosResponse<void>>("shopify/settings", payload).then((res) => res.data);

export const putShopifyUnlink = (): Promise<void> => api.put<void>("shopify/unlink").then((res) => res.data);

export const deletePromotion = (promotionId: string): Promise<void> =>
  api.delete<tPromotion, AxiosResponse<void>>(`promotions/${promotionId}`).then((res) => res.data);

export default api;
